<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import type { UpdateModeSubTypes, UpdateModeTypes } from 'src/stores/ui/edit_lists';

	//Design system components
	import { Modal, CardGrid, SelectCard, Text, Icon, LinkWithIcon } from '@Shelly/shelly-design-system';
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
	import { firstCharCapOnly } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const dispatch = createEventDispatcher<{
		close: void;
		'menu-item-click': { callback: () => void };
		'ai-command-click': { type: UpdateModeTypes; subType: UpdateModeSubTypes };
		'view-item-click': { callback: () => void };
	}>();

	export let showModalMenu: boolean = false;
	export let dropdownMenuMenuData: Array<{
		title: string;
		icon: string;
		callback?: () => void;
		showDropdown?: boolean;
		items?: unknown[];
	}> = [];
	export let isEmptyDeviceList: boolean = false;
	export let AIModelCommands: Array<{
		name: string;
		id: UpdateModeSubTypes;
		icon: string;
	}> = [];
	/**
	 * Optional "Change view" (or similar) group rendered below the bulk-actions
	 * grid. Each item invokes its own `onClick` and closes the modal —
	 * used by `AllDevicesDropdown` so the mobile modal mirrors the desktop
	 * dropdown (bulk actions + view selection in one sheet).
	 */
	export let viewSectionTitle = '';
	export let viewItems: Array<{
		title: string;
		icon: string;
		disabled?: boolean;
		onClick: () => void;
	}> = [];

	let showAiModelOptions = false;

	const closeModalMenu = () => {
		showModalMenu = false;
		showAiModelOptions = false;
		dispatch('close');
	};

	const isSubmenuItem = (menuItemList: { showDropdown?: boolean; items?: unknown[] }) =>
		menuItemList.showDropdown !== undefined && Boolean(menuItemList.items?.length);

	const handleMenuItemClick = (menuItemList: {
		title: string;
		callback?: () => void;
		showDropdown?: boolean;
		items?: unknown[];
	}) => {
		if (isSubmenuItem(menuItemList)) {
			showAiModelOptions = true;
			return;
		}

		if (menuItemList.callback) {
			closeModalMenu();
			dispatch('menu-item-click', { callback: menuItemList.callback });
		}
	};

	const handleAiCommandClick = (modelCommand: { id: UpdateModeSubTypes }) => {
		closeModalMenu();
		dispatch('ai-command-click', { type: 'ai_device_failure', subType: modelCommand.id });
		showAiModelOptions = false;
	};
</script>

<Modal bind:showModal={showModalMenu} on:close={closeModalMenu} hideTitle>
	<svelte:fragment slot="content">
		{#if showAiModelOptions}
			<LinkWithIcon
				class="mb-m"
				textClasses="size-m-button-min-height"
				icon="fa-light fa-chevron-left"
				text={$format('text_button_back', {
					default: 'Back',
				})}
				on:click={() => {
					showAiModelOptions = false;
				}}
			/>
			<CardGrid md={250} noPaddingBottom gridGap="s">
				{#each AIModelCommands as modelCommand}
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						hideAction
						class="width-full"
						type="horizontal"
						title={$format(modelCommand.name)}
						icon={mapIconSolidWeightToLight(modelCommand.icon)}
						on:click={() => handleAiCommandClick(modelCommand)}
					/>
				{/each}
			</CardGrid>
		{:else}
			<CardGrid md={250} noPaddingBottom gridGap="s">
				{#each dropdownMenuMenuData as menuItemList}
					{@const isSubmenu = isSubmenuItem(menuItemList)}
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						hideAction
						class="width-full"
						type="horizontal"
						title={isSubmenu ? menuItemList.title : firstCharCapOnly(menuItemList.title)}
						icon={mapIconSolidWeightToLight(menuItemList.icon)}
						on:click={() => handleMenuItemClick(menuItemList)}
						disabled={isEmptyDeviceList}
						hideRightCustomAction={!isSubmenu}
					>
						<Icon size="s" slot="customActionRight" icon="fa-regular fa-chevron-right" />
					</SelectCard>
				{/each}
			</CardGrid>

			{#if viewItems.length}
				<hr class="mt-l mb-l" />
				{#if viewSectionTitle}
					<Text class="mb-s" styleType="label-large">{viewSectionTitle}</Text>
				{/if}
				<CardGrid md={250} noPaddingBottom gridGap="s">
					{#each viewItems as viewItem}
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							hideAction
							class="width-full"
							type="horizontal"
							title={firstCharCapOnly(viewItem.title)}
							icon={mapIconSolidWeightToLight(viewItem.icon)}
							disabled={viewItem.disabled}
							on:click={() => {
								dispatch('view-item-click', { callback: viewItem.onClick });
								viewItem.onClick();
								closeModalMenu();
							}}
						/>
					{/each}
				</CardGrid>
			{/if}
		{/if}
	</svelte:fragment>
</Modal>
